import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";
import { getErrorMessage } from "../lib/errors";

export default function RateBookingDialog({ booking, open, onOpenChange, onSubmitted }) {
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [review, setReview] = useState("");
  const [busy, setBusy] = useState(false);
  const { t } = useTranslation();

  // Reset form whenever dialog opens for a new booking
  useEffect(() => {
    if (open) { setRating(5); setHover(0); setReview(""); }
  }, [open, booking?.id]);

  const submit = async () => {
    if (!booking) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/review`, { rating, review });
      toast.success("Thanks for your feedback!");
      onOpenChange(false);
      onSubmitted?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to submit review"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm" data-testid="rate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {booking ? t("rate.title") + " " + booking.service_name : t("rate.title")}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#646964]">{t("rate.prompt")}</p>

        <div className="flex justify-center gap-2 my-4">
          {[1,2,3,4,5].map((n) => (
            <button
              key={n}
              type="button"
              data-testid={`star-${n}`}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              onClick={() => setRating(n)}
              className="transition-transform hover:scale-110"
            >
              <Star
                size={36}
                strokeWidth={1.5}
                className={(hover || rating) >= n ? "fill-[#D9734E] text-[#D9734E]" : "text-[#94A3B8]"}
              />
            </button>
          ))}
        </div>

        <Textarea
          placeholder={t("rate.commentPlaceholder")}
          rows={4}
          value={review}
          onChange={(e) => setReview(e.target.value)}
          data-testid="review-text"
          className="rounded-sm"
        />

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} variant="outline" className="rounded-sm">{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={busy || !booking} data-testid="submit-review" className="rounded-sm bg-[#1C4532] hover:bg-[#143324] text-white">
            {busy ? t("rate.submitting") : t("rate.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
